import type {
  CycleRecordDto,
  CycleStartedResponse,
  EmergencyStopResponse,
  LampCommandRequest,
  LampCommandResponse,
  UnitStatusResponse,
} from '@evesix/contracts';
import type { AbortReason, CycleJob, CycleRecord, RecordProblem } from '../domain/types';
import { ApiRequestError, type ApiClient } from './api';

export interface RobotStatus {
  readonly connected: boolean;
  /** Null when unknown. */
  readonly lampOn: boolean | null;
  readonly lampPowerW: number | null;
  /** Epoch ms at which a manually-lit lamp switches itself off. */
  readonly manualAutoOffAt: number | null;
}

export type LampCommand = LampCommandRequest;

export interface LampCommandResult {
  readonly acknowledged: boolean;
  readonly lampOn: boolean;
  readonly autoOffAt: number | null;
}

/** The lamp controller did not confirm it switched off; the lamp may still be lit. */
export class StopNotAcknowledgedError extends Error {
  override readonly name = 'StopNotAcknowledgedError';
}

/** The server did not confirm a completed cycle — so the room is not on record as sanitised. */
export class CycleRecordError extends Error {
  override readonly name = 'CycleRecordError';

  constructor(
    readonly problem: RecordProblem,
    message: string,
  ) {
    super(message);
  }
}

/**
 * The boundary between the UI and the hardware. Commands resolve once the device acknowledges
 * them and reject if it cannot be reached.
 */
export interface RobotGateway {
  getStatus(): RobotStatus;
  /** Subscribes to status changes; returns an unsubscribe function. */
  subscribe(listener: () => void): () => void;
  /**
   * Fires with the server's reason when it ends the cycle this tablet is running — the machine
   * was switched off outside Evesix, the relay failsafe fired, or the power check failed.
   */
  onCycleLost(listener: (reason: AbortReason) => void): () => void;
  startEmission(job: CycleJob): Promise<void>;
  /** Re-attaches to a cycle already running on the server (after a tablet reload). */
  resumeCycle(cycleId: string): void;
  /**
   * Resolves with the server's audit record, or null when there is no server. Rejects with
   * CycleRecordError when the server cannot be reached or refuses the completion.
   */
  completeCycle(record: CycleRecord): Promise<CycleRecord | null>;
  /** Rejects with StopNotAcknowledgedError if the controller did not confirm. */
  emergencyStop(reason: AbortReason, job: CycleJob | null): Promise<void>;
  /** Manual on/off from the lamp control panel. */
  setLamp(command: LampCommand): Promise<LampCommandResult>;
}

const OFF: RobotStatus = Object.freeze({
  connected: true,
  lampOn: false,
  lampPowerW: null,
  manualAutoOffAt: null,
});

/** Stands in for the robot during design review and local development; every command succeeds. */
export class SimulatedRobotGateway implements RobotGateway {
  #status: RobotStatus = OFF;
  #manualTimer: ReturnType<typeof setTimeout> | undefined;
  readonly #listeners = new Set<() => void>();
  readonly #log: (message: string, detail: unknown) => void;
  readonly #now: () => number;

  constructor(
    log: (message: string, detail: unknown) => void = () => undefined,
    now: () => number = Date.now,
  ) {
    this.#log = log;
    this.#now = now;
  }

  getStatus(): RobotStatus {
    return this.#status;
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  onCycleLost(): () => void {
    return () => undefined;
  }

  startEmission(job: CycleJob): Promise<void> {
    this.#log('startEmission', job);
    this.#setLampState(true, null);
    return Promise.resolve();
  }

  resumeCycle(cycleId: string): void {
    this.#log('resumeCycle', cycleId);
    this.#setLampState(true, null);
  }

  completeCycle(record: CycleRecord): Promise<CycleRecord | null> {
    this.#log('completeCycle', record);
    this.#setLampState(false, null);
    return Promise.resolve(null);
  }

  emergencyStop(reason: AbortReason, job: CycleJob | null): Promise<void> {
    this.#log('emergencyStop', { reason, job });
    this.#setLampState(false, null);
    return Promise.resolve();
  }

  setLamp(command: LampCommand): Promise<LampCommandResult> {
    this.#log('setLamp', command);
    const autoOffAt = command.on ? this.#now() + command.seconds * 1000 : null;
    this.#setLampState(command.on, autoOffAt);
    if (command.on) {
      this.#manualTimer = setTimeout(() => this.#setLampState(false, null), command.seconds * 1000);
    }
    return Promise.resolve({ acknowledged: true, lampOn: command.on, autoOffAt });
  }

  #setLampState(lampOn: boolean, manualAutoOffAt: number | null): void {
    clearTimeout(this.#manualTimer);
    this.#status = { ...this.#status, lampOn, manualAutoOffAt };
    for (const listener of this.#listeners) listener();
  }
}

export interface HttpRobotGatewayOptions {
  readonly pollMs?: number;
  /** Pauses between attempts to record a completed cycle. */
  readonly recordRetryDelaysMs?: readonly number[];
  readonly sleep?: (ms: number) => Promise<void>;
}

/** Network trouble or a server error: worth trying again. A 4xx answer is final. */
function isTransient(error: unknown): boolean {
  return !(error instanceof ApiRequestError) || error.status >= 500;
}

const UNKNOWN: RobotStatus = Object.freeze({
  connected: false,
  lampOn: null,
  lampPowerW: null,
  manualAutoOffAt: null,
});

/** Drives the lamp through the control API and polls unit status while anything is listening. */
export class HttpRobotGateway implements RobotGateway {
  #status: RobotStatus = UNKNOWN;
  #activeCycleId: string | null = null;
  /** Bumped whenever #activeCycleId changes, so stale polls cannot report a lost cycle. */
  #generation = 0;
  #timer: ReturnType<typeof setInterval> | undefined;
  readonly #listeners = new Set<() => void>();
  readonly #lostListeners = new Set<(reason: AbortReason) => void>();
  readonly #client: ApiClient;
  readonly #unitPath: string;
  readonly #pollMs: number;
  readonly #recordRetryDelaysMs: readonly number[];
  readonly #sleep: (ms: number) => Promise<void>;

  constructor(
    client: ApiClient,
    unitId: string,
    {
      pollMs = 5000,
      recordRetryDelaysMs = [1000, 2000, 4000, 8000],
      sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    }: HttpRobotGatewayOptions = {},
  ) {
    this.#client = client;
    this.#unitPath = `/units/${encodeURIComponent(unitId)}`;
    this.#pollMs = pollMs;
    this.#recordRetryDelaysMs = recordRetryDelaysMs;
    this.#sleep = sleep;
  }

  getStatus(): RobotStatus {
    return this.#status;
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    if (this.#listeners.size === 1) {
      void this.refresh();
      this.#timer = setInterval(() => void this.refresh(), this.#pollMs);
    }
    return () => {
      this.#listeners.delete(listener);
      if (this.#listeners.size === 0) clearInterval(this.#timer);
    };
  }

  onCycleLost(listener: (reason: AbortReason) => void): () => void {
    this.#lostListeners.add(listener);
    return () => this.#lostListeners.delete(listener);
  }

  resumeCycle(cycleId: string): void {
    this.#setActiveCycle(cycleId);
    this.#setStatus({ ...this.#status, lampOn: true, manualAutoOffAt: null });
  }

  /** Polls status once. Public so callers (and tests) can force a refresh. */
  async refresh(): Promise<void> {
    const generation = this.#generation;
    try {
      const status = await this.#client.get<UnitStatusResponse>(`${this.#unitPath}/status`);
      this.#setStatus({
        connected: status.connected,
        lampOn: status.lampOn,
        lampPowerW: status.lampPowerW,
        manualAutoOffAt: status.manualAutoOffAt ? Date.parse(status.manualAutoOffAt) : null,
      });
      const ours = this.#activeCycleId;
      const lost =
        generation === this.#generation && ours !== null && status.activeCycleId !== ours;
      if (lost) {
        this.#setActiveCycle(null);
        const ended = status.lastEndedCycle?.cycleId === ours ? status.lastEndedCycle : null;
        // Completed elsewhere (e.g. another session finished it): nothing went wrong.
        if (ended?.status === 'completed') return;
        const reason = ended?.abortReason ?? 'DEVICE_FAULT';
        for (const listener of this.#lostListeners) listener(reason);
      }
    } catch {
      this.#setStatus({ ...this.#status, connected: false });
    }
  }

  async startEmission(job: CycleJob): Promise<void> {
    const started = await this.#client.post<CycleStartedResponse>('/cycles', {
      wardCode: job.wardCode,
      bed: job.bed,
      positionId: job.positionId,
      positionLabel: job.positionLabel,
      durationSeconds: job.durationSeconds,
      wallClockSeconds: job.wallClockSeconds,
      channels: job.channels,
    });
    this.#setActiveCycle(started.cycleId);
    this.#setStatus({ ...this.#status, lampOn: true, manualAutoOffAt: null });
  }

  async completeCycle(): Promise<CycleRecord | null> {
    const cycleId = this.#activeCycleId;
    if (!cycleId) return null;
    this.#setActiveCycle(null);
    const record = await this.#recordCompletion(cycleId);
    this.#setStatus({ ...this.#status, lampOn: false });
    return {
      logId: record.logId,
      wardCode: record.wardCode,
      wardName: record.wardName,
      bed: record.bed,
      positionId: record.positionId,
      positionLabel: record.positionLabel,
      operatorName: record.operatorName,
      durationSeconds: record.durationSeconds,
      wallClockSeconds: record.wallClockSeconds,
      channels: record.channels,
      startedAt: record.startedAt,
      completedAt: record.completedAt,
    };
  }

  /**
   * Hospital Wi-Fi drops: network and server errors are retried with backoff (the API answers a
   * repeated completion with the same record). A refusal — e.g. the cycle was interrupted — is final.
   */
  async #recordCompletion(cycleId: string): Promise<CycleRecordDto> {
    const path = `/cycles/${encodeURIComponent(cycleId)}/complete`;
    for (let attempt = 0; ; attempt++) {
      try {
        return await this.#client.post<CycleRecordDto>(path);
      } catch (error) {
        if (!isTransient(error)) {
          throw new CycleRecordError('rejected', (error as Error).message);
        }
        const delay = this.#recordRetryDelaysMs[attempt];
        if (delay === undefined) {
          throw new CycleRecordError('unreachable', 'Could not reach the control service');
        }
        await this.#sleep(delay);
      }
    }
  }

  async emergencyStop(reason: AbortReason): Promise<void> {
    const cycleId = this.#activeCycleId;
    this.#setActiveCycle(null);
    const result = await this.#client.post<EmergencyStopResponse>(
      `${this.#unitPath}/emergency-stop`,
      {
        reason,
        ...(cycleId ? { cycleId } : {}),
      },
    );
    if (!result.lampOff) throw new StopNotAcknowledgedError('Lamp controller did not confirm off');
    this.#setStatus({ ...this.#status, lampOn: false, manualAutoOffAt: null });
  }

  async setLamp(command: LampCommand): Promise<LampCommandResult> {
    const result = await this.#client.post<LampCommandResponse>(`${this.#unitPath}/lamp`, command);
    const autoOffAt = result.autoOffAt ? Date.parse(result.autoOffAt) : null;
    if (result.abortedCycleId) this.#setActiveCycle(null);
    this.#setStatus({ ...this.#status, lampOn: result.lampOn, manualAutoOffAt: autoOffAt });
    return { acknowledged: result.acknowledged, lampOn: result.lampOn, autoOffAt };
  }

  #setActiveCycle(cycleId: string | null): void {
    this.#activeCycleId = cycleId;
    this.#generation++;
  }

  #setStatus(next: RobotStatus): void {
    const prev = this.#status;
    const same =
      prev.connected === next.connected &&
      prev.lampOn === next.lampOn &&
      prev.lampPowerW === next.lampPowerW &&
      prev.manualAutoOffAt === next.manualAutoOffAt;
    if (same) return;
    this.#status = next;
    for (const listener of this.#listeners) listener();
  }
}
